import React from "react";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import { HelmetProvider } from "react-helmet-async";

import "./utils/axiosSetup";
import store from "./redux/store";
import "./index.css";
import App from "./App";
import * as serviceWorker from "./serviceWorker";
import TagManager from "react-gtm-module";
import mountApp from "./lib/mountApp";

const tagManagerArgs = {
  gtmId: "GTM-W8H2BRD",
};

// GTM is loaded in two passes for two distinct reasons:
//
//   1. Skip during react-snap's headless prerender so every
//      route prerender doesn't fire a real pageview during the
//      build (558 fake hits per ship).  UA marker is the
//      puppeteer profile.
//
//   2. Defer the runtime initialise until ``window.load`` so the
//      gtm.js fetch + GTM's own script execution don't compete
//      with React hydration for main-thread cycles.  Search
//      Console flagged INP > 200ms across the site; PageSpeed
//      named GTM among the long main-thread tasks bunching up
//      before the first user tap.  After-load is the canonical
//      analytics-defer pattern: tracking still runs, the user's
//      first interaction just doesn't queue behind it.
const isPrerendering =
  typeof navigator !== "undefined" &&
  /ReactSnap/i.test(navigator.userAgent);
if (!isPrerendering) {
  if (document.readyState === "complete") {
    TagManager.initialize(tagManagerArgs);
  } else {
    window.addEventListener(
      "load",
      () => TagManager.initialize(tagManagerArgs),
      { once: true },
    );
  }
}

// HelmetProvider wraps the entire app so individual route components
// can declare per-route title, meta, and JSON-LD via the SEO
// component without each page needing its own provider.  All
// container tests hit this same setup through renderWithProviders
// in src/test-utils.js.
const app = (
  <HelmetProvider>
    <Provider store={store}>
      <App />
    </Provider>
  </HelmetProvider>
);

// Hydrate when react-snap has prerendered the route into the DOM;
// render fresh otherwise.  See src/lib/mountApp.js for the contract
// and the regression-test rationale.
mountApp(app, document.getElementById("root"), ReactDOM);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
